import type { IIntentItem } from 'src/types/file';

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useIntents } from 'src/hooks/api/upload';
import { useUpdateUserIntent } from 'src/hooks/api/upload/useUpdateIntent';

import { mapDataToIIntentItem } from 'src/utils/utils';
import { FileType, ApiRunConfigStatus } from 'src/utils/enumerations';

import { usePipeline } from './usePipeline';
import { useFilesContext } from './useFilesContext';

export function useFiles() {
  const { allFiles, files, setFiles, searchTerm, setSearchTerm, totalCount, setTotalCount } =
    useFilesContext();
  const queryClient = useQueryClient();
  const { pipelineType } = usePipeline();

  const searchParams = new URLSearchParams(window.location.search);
  const sortBy = searchParams.get('sortBy') || 'created_at';
  const sortOrder = (searchParams.get('sortOrder') as 'desc' | 'asc') || 'desc';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(localStorage.getItem('rowsPerPage') || '10', 10);

  const [paginationOptions, setPaginationOptions] = useState({
    page,
    page_size: pageSize,
    analysis_type: pipelineType,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const { data, isLoading, refetch } = useIntents(paginationOptions);
  const { updateUserIntent } = useUpdateUserIntent();

  /**
   * Effect to update the files and total count state when new data is fetched.
   */
  useEffect(() => {
    if (data) {
      const { results } = data;
      const _files = mapDataToIIntentItem(results || []);
      setFiles(_files);
      setTotalCount(data.total_count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /**
   * Updates the pagination options used asquery parameters and refetches the data.
   * @param {Object} params - The new pagination options for query parameters.
   */
  const updatePaginationOptions = (params: Partial<typeof paginationOptions>) => {
    setPaginationOptions((prevPag) => ({ ...prevPag, ...params }));
  };

  /**
   * Removes the PDF file associated with the given PDF file ID.
   * This function updates the state by setting the `pdfFileId` and `pdfFileName` to null
   * for the file that matches the provided `pdfFileId`.
   *
   * @param {string} intentId - The ID of the intent.
   */
  const removePdfFile = (intentId: string) => {
    queryClient.setQueryData(['intents', paginationOptions], (oldData: any) => {
      if (!oldData) return oldData;
      const { results } = oldData;
      const updatedResults = results.map((intent: any) => {
        if (intent.id === intentId) {
          const { files: intentFiles } = intent;
          const audioFile = intentFiles.find((file: any) => file.extension !== 'pdf');
          // Call updateUserIntent and patch only audio file ID and metadata
          updateUserIntent({
            id: intent.id,
            requestData: {
              title: 'Intent Title',
              file_ids: [audioFile.id],
              intent_metadata: {
                files: [
                  {
                    fileId: audioFile.id,
                    name: audioFile.name,
                    type: FileType.Audio,
                  },
                ],
              },
              pipeline_type: pipelineType,
            },
          });

          return {
            ...intent,
            run_config: null,
            files: [
              {
                id: audioFile.id,
                name: audioFile.name,
                extension: 'mp3', // not relevant, assuming 'mp3' for now
                size: 0,
              },
            ],
          };
        }
        return intent;
      });
      return {
        ...oldData,
        results: updatedResults,
      };
    });
  };

  /**
   * Adds a PDF file associated with the given audio file id.
   * This function updates the state by setting the `pdfFileId` and `pdfFileName`
   * for the file that matches the provided `id`.
   *
   * @param {string} id - The ID of the intent.
   * @param {string} pdfFileId - The ID of the PDF file to be added.
   * @param {string} pdfFileName - The name of the PDF file to be added.
   */
  const addPdfFile = (id: string, runConfigId: string, pdfFileId: string, pdfFileName: string) => {
    queryClient.setQueryData(['intents', paginationOptions], (oldData: any) => {
      if (!oldData) return oldData;
      const { results } = oldData;
      const updatedResults = results.map((intent: any) => {
        if (intent.id === id) {
          return {
            ...intent,
            run_config: {
              id: runConfigId,
              status: ApiRunConfigStatus.InProgress,
              created_at: new Date(),
              updated_at: new Date(),
            },
            files: [
              ...intent.files,
              {
                id: pdfFileId,
                name: pdfFileName,
                size: 0,
                extension: 'pdf',
              },
            ],
          };
        }
        return intent;
      });
      return {
        ...oldData,
        results: updatedResults,
      };
    });
  };

  /**
   * Adds a new intent item to the cached data for optimistic update.
   * This function updates the cache by adding a new intent item to the list of results.
   *
   * @param {IIntentItem} newIntentItem - The new intent item to be added.
   */
  const addIntentItem = (newIntentItem: IIntentItem) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let files = [
      {
        id: newIntentItem.audioFileId,
        name: newIntentItem.audioFileName,
        extension: 'mp3', // not relevant, assuming 'mp3' for now
        size: newIntentItem.audioFileSize,
      },
    ];
    files = newIntentItem.pdfFileId
      ? [
          ...files,
          {
            id: newIntentItem.pdfFileId,
            name: newIntentItem.pdfFileName || '',
            extension: 'pdf',
            size: 0,
          },
        ]
      : files;

    queryClient.setQueryData(['intents', paginationOptions], (oldData: any) => {
      if (!oldData) return oldData;
      const newIntent = {
        id: newIntentItem.id,
        title: '',
        pipeline_type: pipelineType,
        intent_metadata: {},
        run_config: newIntentItem.runConfigId
          ? {
              id: newIntentItem.runConfigId,
              status: ApiRunConfigStatus.InProgress,
              created_at: newIntentItem.created,
              updated_at: newIntentItem.runConfigUpdated,
            }
          : null,
        files,
        created_at: newIntentItem.created,
      };
      // Add the new intent to the top of the list and limit the list to the original length
      const updatedResults = [newIntent, ...oldData.results].slice(0, oldData.results.length);

      return {
        ...oldData,
        results: updatedResults,
      };
    });
  };

  /**
   * Updates the run configuration status in cache of a specific intent file.
   *
   * @param intentId - The unique identifier of the intent file to update.
   * @param status - The new status to set for the run configuration.
   */
  const updateRunConfigStatus = async (intentId: string, status: ApiRunConfigStatus) => {
    queryClient.setQueryData(['intents', paginationOptions], (oldData: any) => {
      if (!oldData) return oldData;
      const updatedResults = oldData.results.map((intent: any) => {
        if (intent.id === intentId) {
          return {
            ...intent,
            run_config: {
              ...intent.run_config,
              updated_at: new Date(),
              status,
            },
          };
        }
        return intent;
      });
      return {
        ...oldData,
        results: updatedResults,
      };
    });
  };

  return {
    files,
    allFiles,
    setFiles,
    searchTerm,
    setSearchTerm,
    removePdfFile,
    addPdfFile,
    addIntentItem,
    isLoading,
    refetchIntents: refetch,
    totalCount,
    updatePaginationOptions,
    paginationOptions,
    updateRunConfigStatus,
  };
}
